/**
 * Notacao da bandeja, gerada do GESTO do jogador.
 *
 * Fase 3 de docs/PLANO_MODULO_DADOS.md. O jogador toca nos dados; a notacao
 * aparece acima da bandeja para ser LIDA, nunca digitada. Por isso aqui nao ha
 * parser — so o caminho de ida.
 *
 * Espelha `backend/services/dice_pool_service.py`, que e quem manda de verdade:
 * este modulo existe para a tela poder mostrar a jogada ANTES de rolar. O que
 * vale no fim e sempre a notacao devolvida pelo servidor.
 */
import type { DiceGroup, DiceRule, DiceType } from '@/types/dice'

const LADOS: Record<DiceType, number> = {
  D4: 4, D6: 6, D8: 8, D10: 10, D12: 12, D20: 20, D100: 100,
}

/** `['D6','D20','D6']` vira `[{type:'D6',qty:2},{type:'D20',qty:1}]`. */
export function agrupar(dados: DiceType[]): DiceGroup[] {
  const conta = new Map<DiceType, number>()
  dados.forEach(d => conta.set(d, (conta.get(d) ?? 0) + 1))
  return [...conta.entries()]
    .sort((a, b) => LADOS[b[0]] - LADOS[a[0]])
    .map(([type, qty]) => ({ type, qty }))
}

/** `2d6 + 1d20`, do maior dado para o menor (como se le em voz alta). */
export function agrupado(dados: DiceType[]): string {
  return agrupar(dados)
    .map(g => `${g.qty}d${LADOS[g.type]}`)
    .join(' + ')
}

export interface Parcela {
  valor: number
  origem: string
}

export function notacao(
  dados: DiceType[],
  regra?: DiceRule | null,
  parcelas: Parcela[] = []
): string {
  if (!dados.length) return ''
  const grupos = agrupado(dados)

  let corpo: string
  if (regra?.tipo === 'maiores') corpo = `(${grupos})kh${regra.n}`
  else if (regra?.tipo === 'sucessos') corpo = `(${grupos})k>${regra.limiar - 1}`
  else corpo = grupos

  parcelas.forEach(p => {
    corpo += ` ${p.valor >= 0 ? '+' : '−'} ${Math.abs(p.valor)} [${p.origem}]`
  })
  return corpo
}

/** Frase da regra da mesa, para o jogador saber como a jogada sera resolvida. */
export function descreverRegra(regra?: DiceRule | null): string {
  if (!regra || regra.tipo === 'somar') return 'somar todos os dados'
  if (regra.tipo === 'maiores') {
    return regra.n === 1 ? 'manter o maior dado' : `manter os ${regra.n} maiores`
  }
  return `contar dados a partir de ${regra.limiar}`
}
